import type {
    IChangePasswordPayload,
    IChangeSecurityResponse,
    IFormResult,
    IUserProfileResponse,
} from "@/src/interfaces/user.interface"
import axiosInstance from "@/src/lib/axios"

/**
 * The signed-in merchant's own account — profile, password, sign-out.
 *
 * Unlike the admin user endpoints these take no {user} segment: every method
 * acts on `Auth::user()`, so a merchant can only ever reach their own record.
 *
 * MerchantController returns plain JSON rather than the ApiResponseTrait
 * envelope the business/role/staff controllers use, so nothing is unwrapped
 * here — see IApiSuccessEnvelope's note about checking the controller first.
 */
class MerchantUserService {
    /**
     * Doubled on purpose. bootstrap/app.php loads routes/merchant.php under a
     * `api/v1/merchant` prefix, and these four routes declare their own
     * `merchant/` prefix on top of it, so the live URLs really are
     * `api/v1/merchant/merchant/…` (confirmed with `php artisan route:list`).
     * The sibling business/roles/staff routes don't, which is why they sit at
     * a single `/merchant/…`. Drop a segment here if the backend drops one.
     */
    private readonly baseEndpoint = "/merchant/merchant"

    /** The authenticated merchant's own profile (GET merchant/user-profile). */
    async fetchProfile(signal?: AbortSignal): Promise<IUserProfileResponse> {
        const { data } = await axiosInstance.get<IUserProfileResponse>(
            `${this.baseEndpoint}/user-profile`,
            { signal }
        )
        return data
    }

    /**
     * The account data behind the change-password screen (GET
     * merchant/change-security). Same payload as the profile endpoint, kept
     * separate because the backend does — it re-reads the user so the form
     * never acts on a stale copy.
     */
    async fetchChangeSecurity(signal?: AbortSignal): Promise<IChangeSecurityResponse> {
        const { data } = await axiosInstance.get<IChangeSecurityResponse>(
            `${this.baseEndpoint}/change-security`,
            { signal }
        )
        return data
    }

    /**
     * Set a new password (PATCH merchant/change-password).
     *
     * The API validates `confirmed` + Password::defaults(), and does not ask
     * for the current password — the bearer token is the proof of identity.
     * Existing tokens stay valid, so the session survives the change.
     */
    async changePassword(payload: IChangePasswordPayload): Promise<IFormResult> {
        const { data } = await axiosInstance.patch<IFormResult>(
            `${this.baseEndpoint}/change-password`,
            payload
        )
        return data
    }

    /**
     * Revokes the current Sanctum token (POST merchant/logout). Throttled to
     * 60/min server-side. Must run before the token is cleared client-side.
     */
    async logout(): Promise<void> {
        await axiosInstance.post(`${this.baseEndpoint}/logout`)
    }
}

// Singleton export — one instance shared across the app
export const merchantUserService = new MerchantUserService()
